import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, logout, getUsername } from '../api/auth';
import { useTranslation } from '../context/TranslationContext';
import { useModules } from '../modules/useModules';
import BugReportModal from '../bug-report/BugReportModal';
import Icon from '../ui/Icon';
import styles from './Sidebar.module.css';

/**
 * Sidebar — the calm, type-led nav per PLAN §3.2.
 *
 * Behavior preserved from the previous implementation:
 * - Brand routes to /dashboard when authed, / otherwise.
 * - Re-renders on `auth-state-change` window events.
 * - Mobile (≤991px): hamburger button opens an overlay drawer.
 * - Bottom: TR / EN segmented toggle, user identity, bug report, logout.
 *
 * Structural change (decision in IMPLEMENTATION §3.2): module groups
 * are FLATTENED — no expand/collapse. The uppercase module name is a
 * section label (non-interactive), its entries always render below it.
 */
export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useTranslation();
  const { modules } = useModules();

  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [username, setUsername] = useState(getUsername());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);

  const INSIGHTS_PATHS = ['/reports'];
  const API_PATHS = ['/apikey'];

  const matchesGroup = (entries, pathname) =>
    entries.some(
      (e) => pathname === e.path || pathname.startsWith(e.path + '/')
    );

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const set = new Set();
    modules.forEach((m) => {
      if (matchesGroup(m.navEntries || [], location.pathname)) set.add(m.id);
    });
    if (matchesGroup(INSIGHTS_PATHS.map((p) => ({ path: p })), location.pathname))
      set.add('insights');
    if (matchesGroup(API_PATHS.map((p) => ({ path: p })), location.pathname))
      set.add('api');
    return set;
  });

  const toggleGroup = (id) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const collapseAll = () => setExpandedGroups(new Set());
  const anyExpanded = expandedGroups.size > 0;

  useEffect(() => {
    const handler = (e) => {
      setAuthenticated(e.detail.isAuthenticated);
      setUsername(e.detail.username);
    };
    window.addEventListener('auth-state-change', handler);
    setAuthenticated(isAuthenticated());
    setUsername(getUsername());
    return () => window.removeEventListener('auth-state-change', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setAuthenticated(false);
    setUsername(null);
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const sortedModules = useMemo(
    () => [...modules].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [modules]
  );

  // Auto-expand the group containing the newly active route.
  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      sortedModules.forEach((m) => {
        if (matchesGroup(m.navEntries || [], location.pathname)) next.add(m.id);
      });
      if (matchesGroup(INSIGHTS_PATHS.map((p) => ({ path: p })), location.pathname))
        next.add('insights');
      if (matchesGroup(API_PATHS.map((p) => ({ path: p })), location.pathname))
        next.add('api');
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, sortedModules]);

  const initial = (username || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <>
      <button
        className={styles.hamburger}
        onClick={() => setMobileOpen(true)}
        aria-label={t('openMenu')}
        type="button"
      >
        <Icon name="fa-bars" size="lg" />
      </button>

      <div
        className={`${styles.overlay} ${mobileOpen ? styles.overlayVisible : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      <nav
        className={`${styles.sidebar} ${mobileOpen ? styles.sidebarOpen : ''}`}
        aria-label="Main navigation"
      >
        <Link
          to={authenticated ? '/dashboard' : '/'}
          className={styles.brand}
        >
          <span className={styles.brandIcon}>
            <Icon name="fa-scale-balanced" size="lg" strokeWidth={2} />
          </span>
          <span className={styles.brandText}>{t('appName')}</span>
        </Link>

        <div className={styles.nav}>
          {authenticated && anyExpanded && (
            <button
              type="button"
              className={styles.collapseAllBtn}
              onClick={collapseAll}
              title={t('collapseAll')}
            >
              <Icon name="fa-angle-double-left" size={11} />
              <span>{t('collapseAll')}</span>
            </button>
          )}
          {authenticated ? (
            <>
              <Link
                to="/dashboard"
                className={`${styles.link} ${isActive('/dashboard') ? styles.linkActive : ''}`}
              >
                <Icon name="fa-tachometer-alt" />
                <span>{t('dashboard')}</span>
              </Link>

              {sortedModules.map((m) => {
                const entries = [...(m.navEntries || [])].sort(
                  (a, b) => (a.order || 0) - (b.order || 0)
                );
                if (entries.length === 0) return null;
                const open = expandedGroups.has(m.id);

                return (
                  <div key={m.id} className={styles.group}>
                    <button
                      type="button"
                      className={styles.groupLabelBtn}
                      onClick={() => toggleGroup(m.id)}
                      aria-expanded={open}
                    >
                      <span>
                        {t(m.displayNameKey).toLocaleUpperCase(
                          language === 'tr' ? 'tr-TR' : undefined
                        )}
                      </span>
                      <Icon
                        name="fa-chevron-right"
                        size="sm"
                        className={`${styles.groupChevron} ${open ? styles.groupChevronOpen : ''}`}
                      />
                    </button>
                    <div className={`${styles.groupEntries} ${open ? styles.groupEntriesOpen : ''}`}>
                      <div className={styles.groupEntriesInner}>
                        {entries.map((e) => (
                          <Link
                            key={e.path}
                            to={e.path}
                            className={`${styles.link} ${
                              isActive(e.path) ? styles.linkActive : ''
                            }`}
                          >
                            {e.icon && <Icon name={e.icon} />}
                            <span>{t(e.labelKey)}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {(() => {
                const open = expandedGroups.has('insights');
                return (
                  <div className={styles.group}>
                    <button
                      type="button"
                      className={styles.groupLabelBtn}
                      onClick={() => toggleGroup('insights')}
                      aria-expanded={open}
                    >
                      <span>
                        {t('insights').toLocaleUpperCase(
                          language === 'tr' ? 'tr-TR' : undefined
                        )}
                      </span>
                      <Icon
                        name="fa-chevron-right"
                        size="sm"
                        className={`${styles.groupChevron} ${open ? styles.groupChevronOpen : ''}`}
                      />
                    </button>
                    <div className={`${styles.groupEntries} ${open ? styles.groupEntriesOpen : ''}`}>
                      <div className={styles.groupEntriesInner}>
                        <Link
                          to="/reports"
                          className={`${styles.link} ${isActive('/reports') ? styles.linkActive : ''}`}
                        >
                          <Icon name="fa-chart-line" />
                          <span>{t('reports')}</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const open = expandedGroups.has('api');
                return (
                  <div className={styles.group}>
                    <button
                      type="button"
                      className={styles.groupLabelBtn}
                      onClick={() => toggleGroup('api')}
                      aria-expanded={open}
                    >
                      <span>{t('apiSection')}</span>
                      <Icon
                        name="fa-chevron-right"
                        size="sm"
                        className={`${styles.groupChevron} ${open ? styles.groupChevronOpen : ''}`}
                      />
                    </button>
                    <div className={`${styles.groupEntries} ${open ? styles.groupEntriesOpen : ''}`}>
                      <div className={styles.groupEntriesInner}>
                        <Link
                          to="/apikey"
                          className={`${styles.link} ${isActive('/apikey') ? styles.linkActive : ''}`}
                        >
                          <Icon name="fa-key" />
                          <span>{t('apiKey')}</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <>
              <Link
                to="/login"
                className={`${styles.link} ${isActive('/login') ? styles.linkActive : ''}`}
              >
                <Icon name="fa-sign-in-alt" />
                <span>{t('login')}</span>
              </Link>
              <Link
                to="/signup"
                className={`${styles.link} ${isActive('/signup') ? styles.linkActive : ''}`}
              >
                <Icon name="fa-user-plus" />
                <span>{t('signup')}</span>
              </Link>
            </>
          )}
        </div>

        <div className={styles.bottom}>
          <div className={styles.langToggle} role="group" aria-label="Language">
            <button
              type="button"
              className={`${styles.langBtn} ${language === 'tr' ? styles.langBtnActive : ''}`}
              onClick={() => setLanguage('tr')}
            >
              TR
            </button>
            <button
              type="button"
              className={`${styles.langBtn} ${language === 'en' ? styles.langBtnActive : ''}`}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
          </div>

          {authenticated && (
            <>
              <div className={styles.user}>
                <span className={styles.userAvatar}>{initial}</span>
                <span className={styles.userName}>
                  {t('greeting', { name: username || '' })}
                </span>
              </div>

              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setShowBugReport(true)}
              >
                <Icon name="fa-bug" />
                <span>{t('reportBugButton')}</span>
              </button>

              <button
                type="button"
                className={styles.linkButton}
                onClick={handleLogout}
              >
                <Icon name="fa-sign-out-alt" />
                <span>{t('logout')}</span>
              </button>
            </>
          )}
        </div>
      </nav>

      <BugReportModal
        show={showBugReport}
        onClose={() => setShowBugReport(false)}
        isAuthenticated={authenticated}
      />
    </>
  );
}
